import { UseInfiniteQueryResult } from "@tanstack/react-query";
import { AxiosPromise } from "axios";
import { ButtonText } from "components/Atoms/Button";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import React from "react";
import PickerList, { HasKeywordAndPagination, ModalPickerProps } from "./PickerList";
import { RemovableLabelList } from "./RemovableLabelList";

type ModalPickerPropsWithoutOnItemSelected<T, TId extends number | string, TInstruction extends HasKeywordAndPagination> = Omit<ModalPickerProps<T, TId, TInstruction>, "onClick" | "api" | "getError">;

interface Props<T, TId extends number | string, TInstruction extends HasKeywordAndPagination> extends ModalPickerPropsWithoutOnItemSelected<T, TId, TInstruction> {
  value: T[]
  getLabel: (item: T) => string | JSX.Element
  onChange: (newValue: T[]) => void
  hideKeywordFilter?: boolean

  api: (instruction: TInstruction) => AxiosPromise<server.dto.ApiResponseWrapperWithRequest<T[], unknown>>;
  getError: (query: UseInfiniteQueryResult<server.dto.ApiResponseWrapperWithRequest<T[], unknown>>) => string
}

const MultiPickerListWithSelectedItems = <T, TId extends number | string, TInstruction extends HasKeywordAndPagination>(props: React.PropsWithChildren<Props<T, TId, TInstruction>>) => {
  const { value, getLabel, onChange, excludeKeys, api, getError, ...rest } = props;

  const checkedSet = new Set(value.map(i => props.getId(i)));
  const checkedOrExclude = [...checkedSet];
  if (excludeKeys) {
    excludeKeys.forEach(k => checkedOrExclude.push(k));
  }

  const onChecked = (item: T, checked: boolean) => {
    if (checked) {
      onChange([...value, item]);
    } else {
      onChange(value.filter(i => props.getId(i) !== props.getId(item)));
    }
  }

  const onRemove = (item: T) => {
    props.onChange(value.filter(i => props.getId(i) !== props.getId(item)));
  }

  const onClear = () => {
    props.onChange([]);
  }

  return <PickerList
    isMult
    onClick={item => onChecked(item, !checkedSet.has(props.getId(item)))}
    {...rest}
    api={api}
    getError={getError}
    excludeKeys={checkedOrExclude}
    renderListItem={item => (
      <Checkbox
        checked={checkedSet.has(props.getId(item))}
        onChange={(_, checked) => onChecked(item, checked)}
        label={rest.renderListItem(item)}
      />
    )}
    renderBottomPart={(displaySimplifiedView) => {
      if (displaySimplifiedView) {
        return null;
      }

      return <div className="removableLabel-list-wrapper padding flex-column justify-space-between grow1">
        <div className="removableLabel-list-content scrollbox-auto">
          <RemovableLabelList
            value={value}
            renderLabelContent={props.getLabel}
            getId={props.getId}
            removeItem={onRemove}
          />
        </div>
        {value.length > 0 && <div className="flex justify-end">
          <ButtonText label={RESX.GeneralLabels.clear}
            onClick={() => onClear()}
            style={{ padding: 0 }}
          />
        </div>}
      </div>
    }}
  />
};

export default MultiPickerListWithSelectedItems;